import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    MobileIcon,
} from "../Styles/InputStyles";
import { MdClose, MdEdit } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import SelectOptionModal from "../SelectOptionModal";
import { FaEdit } from "react-icons/fa";

const vendorSchema = Yup.object().shape({
    fullName: Yup.string()
        .matches(/^[A-Za-z0-9. ]+$/, "Only letters, numbers, spaces & dots are allowed.")
        .min(4, "Name must be at least 4 characters long")
        .required("Vendor name is required"),
    mobile: Yup.string()
        .matches(/^\d{10}$/, "Mobile number must be 10 digits")
        .nullable(),
    type: Yup.number().optional()
});

const AddVendor = ({ addVendorMutate, addVendorLoading, handleClose }) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);

    const vendorTypes = useSelector((state) => state.VendorTypesReducer.vendorTypes);

    const vendorTypeOptions = vendorTypes
        ?.filter(type => type?.name && type?.id)
        .map(type => ({
            label: type.name,
            value: type.id,
        }));

    const getTypeLabel = (typeValue) => {
        const type = vendorTypeOptions?.find(option => option.value === typeValue);
        return type ? type.label : "Select type...";
    };

    const initialValues = {
        fullName: "",
        mobile: "",
        type: 1,
    }

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleSubmit = (values) => {
        addVendorMutate({
            name: values.fullName,
            mobile: values.mobile,
            type: values.type,
        });
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Add Vendor</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>
            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={vendorSchema}
                >
                    {({ values, setFieldValue, errors, touched }) => {

                        const handleTypeSelect = (selectedValue) => {
                            const selectedType = vendorTypeOptions.find(type => type.value === selectedValue);
                            setFieldValue("type", selectedValue);
                        };

                        const currentTypeLabel = getTypeLabel(values.type);

                        return (
                            <>
                                <Form>
                                    <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                        <Wrapper>
                                            <InputGroup>
                                                <Label htmlFor="fullName">Vendor Name *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="fullName"
                                                        id="fullName"
                                                        autoComplete="off"
                                                    />
                                                </FieldWrappers>
                                                {errors.fullName && touched.fullName && (
                                                    <ErrorMessage>{errors.fullName}</ErrorMessage>
                                                )}
                                            </InputGroup>
                                            <InputGroup>
                                                <Label htmlFor="mobile">Vendor Mobile </Label>
                                                <FieldWrappers>
                                                    <MobileIcon />
                                                    <Field
                                                        type="text"
                                                        name="mobile"
                                                        id="mobile"
                                                        autoComplete="off"
                                                        maxLength={10}
                                                        className="pl-5"
                                                    />
                                                </FieldWrappers>
                                                {errors.mobile && touched.mobile && (
                                                    <ErrorMessage>{errors.mobile}</ErrorMessage>
                                                )}
                                            </InputGroup>

                                            <InputGroup>
                                                <Label htmlFor="type">Type</Label>
                                                <FieldWrappers onClick={() => setIsTypeModalOpen(true)}>

                                                    <Field
                                                        type="text"
                                                        name="typeDisplay"
                                                        id="typeDisplay"
                                                        autoComplete="off"
                                                        readOnly
                                                        value={currentTypeLabel}
                                                        className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                    />
                                                    <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />

                                                </FieldWrappers>

                                            </InputGroup>
                                        </Wrapper>
                                    </ScrollableContent>
                                    <ActionSection>
                                        <SideCancelBtn type="button" onClick={handleClose}>
                                            Cancel
                                        </SideCancelBtn>
                                        <SubmitBtn type="submit" disabled={addVendorLoading}>
                                            {addVendorLoading ? "Submiting..." : "Submit"}
                                        </SubmitBtn>
                                    </ActionSection>
                                </Form>

                                <SelectOptionModal
                                    isOpen={isTypeModalOpen}
                                    onClose={() => setIsTypeModalOpen(false)}
                                    title="Select Vendor Type"
                                    options={vendorTypeOptions}
                                    selectedValue={values.type}
                                    onSelect={handleTypeSelect}
                                    searchPlaceholder="Search vendor types..."
                                />
                            </>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;
export default AddVendor;